import { BaseElement, toString } from '@webtides/element-js';
import BuyBoxEvents from '../../structures/_buy-box/buy-box/BuyBoxEvents';
import SelectedSpecs from '../../structures/_buy-box/SelectedSpecs';
import qs from 'qs/dist/qs';
import ShoppingCartEvents from '../../structures/_shopping-cart/ShoppingCartEvents';
import LastProductCookie from '../../../js/cookies/last-products';
import ProductPageEvents from './ProductPageEvents';

class ProductPage extends BaseElement {
    async connected() {
        this._metaDescription = document.querySelector('[ref="meta-description"]');
        await this.loadCompleteProduct();
    }

    properties() {
        return {
            product: null,
            shopName: '',
            metaText: '',
            selectedVariant: null,
            apiUrl: '/.rest/product/fetch/',
            addUrl: '',
        };
    }

    watch() {
        return {
            selectedVariant: selectedVariant => {
                if (selectedVariant) {
                    this.$refs.toCartAddedVariant.variant = {
                        ...selectedVariant,
                        name: this.product?.name,
                    };

                    this.$refs.availabilitySearchVariant.variant = {
                        ...selectedVariant,
                        name: this.product?.name,
                    };

                    this.$refs.availabilitySearchVariantButton.selectedVariant = selectedVariant;

                    // reset search modal or fire new search
                    if (this.$refs.retailerSearch?.canSearch) {
                        this.$refs.retailerSearch?.fetchRetailers();
                    } else if (this.$refs.retailerSearch && this.$refs.retailerSearch.reset) {
                        this.$refs.retailerSearch.reset();
                    }
                    this.updateUrl(selectedVariant);
                }
            },
        };
    }

    events() {
        return {
            this: {
                [BuyBoxEvents.VARIANT_CHANGE]: ({ detail: selectedVariant }) => {
                    this.selectedVariant = selectedVariant;
                },
                [BuyBoxEvents.ADD_TO_CART]: async ({ detail: selectedVariant }) => {
                    const url = `${this.addUrl}?sku=${selectedVariant.sku}`;
                    try {
                        const response = await fetch(url);
                        if (!response.ok) {
                            throw Error(response.statusText);
                        }
                        const responseBody = await response.json();
                        const responseState = responseBody.state;
                        this.toggleContent(responseState);
                        this.dispatch(ShoppingCartEvents.CART_CHANGED, responseBody.cart, true);
                        this.$refs.toCartAddedModal?.open();
                    } catch (e) {
                        console.warn(e);
                    }
                },
            },
        };
    }

    async loadCompleteProduct() {
        try {
            const response = await fetch(this.apiUrl);
            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            // TODO: data needs to be parse to browser, because data contains html entities which will not be unescaped.
            const responseJson = JSON.stringify(await response.json());
            const doc = new DOMParser().parseFromString(responseJson, 'text/html');

            const completeProduct = JSON.parse(doc.documentElement.textContent);
            this.product = completeProduct;

            this.selectedVariant = completeProduct?.selectedVariant;
            this.$refs.buyBox.product = completeProduct;
            this.$refs.availabilitySearchVariantButton.product = completeProduct;

            this.$refs.toCartAddedVariantSlider.variants = completeProduct?.relatedProducts ?? [];
            if (this.$refs.toCartAddedVariantSlider.variants.length > 0) {
                this.$refs.productRecommendations.classList.remove('hidden');
            }

            this.dispatch(ProductPageEvents.PRODUCT_COMPLETELY_LOADED, completeProduct, true);
        } catch (e) {
            console.log(e);
        }
    }

    toggleContent(state) {
        if (state === 'OK') {
            this.$refs.headlineToCartAdded.classList.remove('hidden');
            this.$refs.headlineNotToCartAdded.classList.add('hidden');
            this.$refs.noticeNotToCartAdded.classList.add('hidden');
        } else if (state === 'NOT_ADDED') {
            this.$refs.headlineToCartAdded.classList.add('hidden');
            this.$refs.headlineNotToCartAdded.classList.remove('hidden');
            this.$refs.noticeNotToCartAdded.classList.remove('hidden');
        }
    }

    get contextPath() {
        return window.siteConfig().contextPath;
    }

    get siteName() {
        return window.siteConfig()?.siteName;
    }

    updateUrl(selectedVariant) {
        const specs = new SelectedSpecs(selectedVariant.specCombinationKey);

        const searchParams = qs.parse(location.search?.substring(1) ?? '');
        const query = qs.stringify({ ...searchParams, ...specs.values });

        const title = `${this.product?.name}`;
        const url = `${window.location.origin}${window.location.pathname}?${query}`;
        history.replaceState(selectedVariant, title, url);

        if (this.product?.productGroupSlug) {
            const {
                specs: { FARF, RAICO },
            } = selectedVariant;

            LastProductCookie.add(this.product.productGroupSlug, {
                frame: RAICO?.attributeKey,
                color: FARF?.attributeKey,
            });
        }

        this.querySelector('social-share-dialog').setAttribute('share-link', url);
    }
}

customElements.define('product-page', ProductPage);
